/**
 * Adventure Hub Page
 *
 * Entry point for co-op adventure mode. Shows active adventure resume card,
 * available world selection, and partner finding (create/join).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdventureStore, type InviteCodeLookup } from '../stores/useAdventureStore';
import { useAuth } from '../contexts/AuthContext';
import { AdventureWorldCard } from '../components/CoOp/AdventureWorldCard';
import { PageAtmosphere } from '../components/atmosphere';
import './AdventureHubPage.css';

export const AdventureHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const {
    adventure,
    availableWorlds,
    isLoading,
    isLoadingWorlds,
    error,
    fetchAvailableWorlds,
    fetchActiveAdventure,
    createAdventure,
    joinAdventure,
    lookupInviteCode,
  } = useAdventureStore();

  const [selectedWorldId, setSelectedWorldId] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [invitePreview, setInvitePreview] = useState<InviteCodeLookup | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  // Fetch worlds on mount (public endpoint, no auth needed)
  useEffect(() => {
    fetchAvailableWorlds();
  }, [fetchAvailableWorlds]);

  // Fetch active adventure after auth is ready
  useEffect(() => {
    if (authLoading || !user) return;

    const timer = setTimeout(() => {
      fetchActiveAdventure();
    }, 100);

    return () => clearTimeout(timer);
  }, [authLoading, user, fetchActiveAdventure]);

  // Lookup invite code with debounce
  useEffect(() => {
    if (!inviteCode || inviteCode.length < 4 || !user) {
      setInvitePreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      const result = await lookupInviteCode(inviteCode);
      setInvitePreview(result);
    }, 500);

    return () => clearTimeout(timer);
  }, [inviteCode, lookupInviteCode, user]);

  const handleSelectWorld = useCallback((worldId: string) => {
    setSelectedWorldId(prev => prev === worldId ? null : worldId);
  }, []);

  const handleCreateAdventure = useCallback(async () => {
    if (!selectedWorldId || isCreating) return;
    setIsCreating(true);
    setJoinError(null);
    try {
      const result = await createAdventure(selectedWorldId);
      navigate(`/adventure/lobby/${result.adventureId}`);
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Failed to create adventure');
    } finally {
      setIsCreating(false);
    }
  }, [selectedWorldId, isCreating, createAdventure, navigate]);

  const handleSoloAdventure = useCallback(async () => {
    if (!selectedWorldId || isCreating) return;
    setIsCreating(true);
    setJoinError(null);
    try {
      const result = await createAdventure(selectedWorldId, { isSolo: true });
      navigate(`/adventure/map/${result.adventureId}`);
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Failed to create solo adventure');
    } finally {
      setIsCreating(false);
    }
  }, [selectedWorldId, isCreating, createAdventure, navigate]);

  const handleJoinAdventure = useCallback(async () => {
    if (!inviteCode.trim() || isJoining) return;
    setIsJoining(true);
    setJoinError(null);
    try {
      const result = await joinAdventure(inviteCode.trim());
      navigate(`/adventure/map/${result.adventureId}`);
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Failed to join adventure');
    } finally {
      setIsJoining(false);
    }
  }, [inviteCode, isJoining, joinAdventure, navigate]);

  const handleResume = useCallback(() => {
    if (!adventure) return;
    navigate(`/adventure/map/${adventure.adventureId}`);
  }, [adventure, navigate]);

  // Loading state
  if (authLoading) {
    return (
      <PageAtmosphere page="adventure-hub">
        <div className="adventure-hub">
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>Authenticating...</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <PageAtmosphere page="adventure-hub">
        <div className="adventure-hub">
          <div className="loading-container">
            <p>Please log in to access adventures.</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  const hasActiveAdventure = adventure &&
    (adventure.status === 'active' || adventure.status === 'paused');

  return (
    <PageAtmosphere page="adventure-hub">
      <div className="adventure-hub">
        <div className="adventure-hub-header">
          <h1>Co-Op Adventures</h1>
          <p className="adventure-hub-subtitle">
            Team up with a partner to conquer themed worlds
          </p>
        </div>

      {/* Error display */}
      {(error || joinError) && (
        <div className="adventure-error">
          <p>{error || joinError}</p>
          {joinError && (
            <button onClick={() => setJoinError(null)}>Dismiss</button>
          )}
        </div>
      )}

      {/* Resume Card */}
      {hasActiveAdventure && (
        <div className="resume-card">
          <div className="resume-card-header">
            <h2>Active Adventure</h2>
            <span className={`status-badge status-${adventure.status}`}>
              {adventure.status}
            </span>
          </div>
          <div className="resume-card-body">
            <div className="resume-stats">
              <div className="resume-stat">
                <span className="resume-stat-value">{adventure.encountersDefeated}</span>
                <span className="resume-stat-label">Encounters</span>
              </div>
              <div className="resume-stat">
                <span className="resume-stat-value">
                  {'\u2605'.repeat(adventure.totalStars)}
                </span>
                <span className="resume-stat-label">{adventure.totalStars} Stars</span>
              </div>
            </div>
            <button className="resume-button" onClick={handleResume}>
              Resume Adventure
            </button>
          </div>
        </div>
      )}

      {/* World Selection */}
      {!hasActiveAdventure && (
        <>
          <div className="section-divider">
            <span>Available Worlds</span>
          </div>

          {isLoadingWorlds ? (
            <div className="loading-container" style={{ minHeight: '20vh' }}>
              <div className="loading-spinner" />
              <p>Loading worlds...</p>
            </div>
          ) : availableWorlds.length === 0 ? (
            <div className="empty-state">
              <p>No adventure worlds available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="worlds-grid">
              {availableWorlds.map(world => (
                <AdventureWorldCard
                  key={world.worldId}
                  worldId={world.worldId}
                  name={world.name}
                  narrative={world.narrative}
                  theme={world.theme}
                  difficulty={world.difficulty}
                  selected={selectedWorldId === world.worldId}
                  onSelect={handleSelectWorld}
                />
              ))}
            </div>
          )}

          {/* Partner Finding Section */}
          <div className="section-divider">
            <span>Find a Partner</span>
          </div>

          <div className="partner-section">
            <div className="partner-create">
              <h3>Create New Adventure</h3>
              <p>Select a world above, then create an adventure to get an invite code.</p>
              <button
                className="create-button"
                onClick={handleCreateAdventure}
                disabled={!selectedWorldId || isCreating || isLoading}
              >
                {isCreating ? 'Creating...' : 'Create Adventure'}
              </button>
              <button
                className="create-button solo-button"
                onClick={handleSoloAdventure}
                disabled={!selectedWorldId || isCreating || isLoading}
                style={{ marginTop: '8px' }}
              >
                {isCreating ? 'Creating...' : 'Solo Adventure'}
              </button>
            </div>

            <div className="partner-divider">
              <span>or</span>
            </div>

            <div className="partner-join">
              <h3>Join with Invite Code</h3>
              <div className="invite-input-row">
                <input
                  type="text"
                  className="invite-input"
                  placeholder="Enter invite code..."
                  value={inviteCode}
                  onChange={e => setInviteCode(e.target.value.toUpperCase())}
                  maxLength={10}
                />
                <button
                  className="join-button"
                  onClick={handleJoinAdventure}
                  disabled={!inviteCode.trim() || isJoining || isLoading}
                >
                  {isJoining ? 'Joining...' : 'Join'}
                </button>
              </div>

              {/* Invite preview */}
              {invitePreview && (
                <div className="invite-preview">
                  <p><strong>{invitePreview.worldName}</strong></p>
                  <p>Host: {invitePreview.hostDisplayName}</p>
                  <p className="invite-preview-status">Status: {invitePreview.status}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
    </PageAtmosphere>
  );
};
